"use client";

/**
 * @file src/app/resume-check/phases/ResultsPhase.tsx
 *
 * Results phase for the /resume-check tool: the same match card the resume page shows (keywords, competencies and
 * details), with the job description and a sign-up CTA below. Public, nothing is saved.
 */

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MatchBreakdown } from "@/components/fit/MatchBreakdown";
import { trackEvent } from "@/lib/logger-client";
import { looksLikeMarkdown } from "@/lib/utils/looksLikeMarkdown";
import type { ResumeMatchReport } from "@/types";

interface ResultsPhaseProps {
  report: ResumeMatchReport;
  jobDescription: string;
  onBack: () => void;
}

const SCORE_HELP = {
  keywords:
    "How well your resume covers the job's keywords. Keywords stated outright count fully, related ones count partly, and core keywords count more.",
  competencies:
    "How well your resume shows what the job asks you to do. A specific example counts fully, a passing mention counts half, and core requirements count more.",
};

export function ResultsPhase({ report, jobDescription, onBack }: ResultsPhaseProps) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <div className="max-w-2xl mx-auto px-6 py-10 space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full px-1"
      >
        <ArrowLeft className="w-4 h-4" />
        Check another job
      </button>

      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: shouldReduceMotion ? 0.01 : 0.4 }}
      >
        <MatchBreakdown
          subject="resume"
          variant="card"
          expanded
          large
          scores={{ keywords: report.keywordScore ?? null, competencies: report.semanticScore ?? null }}
          help={SCORE_HELP}
          terms={report.terms ?? []}
          competencies={(report.requirements ?? []).filter((r) => !r.logistics)}
          testId="resume-check-match"
        />
      </motion.div>

      <section aria-labelledby="resume-check-job-heading">
        <h2 id="resume-check-job-heading" className="text-sm font-semibold text-foreground mb-2 px-1">
          Job description
        </h2>
        <div
          data-testid="resume-check-job"
          className="prose prose-sm max-w-none max-h-72 overflow-y-auto rounded-lg border border-border bg-surface px-4 py-3"
        >
          {looksLikeMarkdown(jobDescription) ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{jobDescription}</ReactMarkdown>
          ) : (
            <p className="whitespace-pre-wrap">{jobDescription}</p>
          )}
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: shouldReduceMotion ? 0.01 : 0.4, delay: shouldReduceMotion ? 0 : 0.2 }}
        className="rounded-2xl bg-primary text-primary-foreground p-8 text-center space-y-4"
      >
        <h3 className="text-xl font-bold">Close these gaps with a tailored resume</h3>
        <p className="text-primary-foreground/80 max-w-md mx-auto text-sm leading-relaxed">
          Hirecarta rewrites your resume for every job, then fills the application.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <a
            href="/login"
            id="resume-check-cta-signup"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-foreground text-primary font-semibold text-sm hover:bg-primary-foreground/90 transition-colors"
            onClick={() => {
              trackEvent("cta_click", {
                event_category: "conversion",
                event_label: "resume_check_results_signup",
                page_location: window.location.href,
                button_text: "Fix my resume free",
                button_position: "resume_check_results",
              });
            }}
          >
            Fix my resume free
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </motion.div>
    </div>
  );
}
